import Analytics from '@/utils/analytics';
import CartIcon from '@assets/icons/cart.svg';
import i18n from '@/utils/i18n';
import {
  shouldShowOrder,
  useNewOrderActionsContext,
} from '@/contexts/new-order-actions-context';
import { useState } from 'react';
import { useToasts } from '@/contexts/toast-context';

import { ActivityIndicator } from '@/components/activity-indicator';
import { APP_CONTAINER_ROUTES } from '@/navigators/routes';
import { AppContainerStackParams } from '@/navigators/types';
import { ColorValue, StyleSheet, TouchableOpacity, View } from 'react-native';
import {
  CompositeNavigationProp,
  useNavigation,
} from '@react-navigation/native';
import {
  ICON_MARGIN,
  ICON_WIDTH,
} from '@/components/navigation-header-content';
import { PerfTiming, PerfTimingEvent } from '@/utils/perf-timing';
import { StackNavigationProp } from '@react-navigation/stack';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import {
  useOrderButtonText,
  useShowCartButton,
} from '@/hooks/use-order-button-text';

type Navigation = CompositeNavigationProp<
  StackNavigationProp<AppContainerStackParams, APP_CONTAINER_ROUTES.Base>,
  StackNavigationProp<AppContainerStackParams, APP_CONTAINER_ROUTES.NewOrder>
>;

interface Props {
  fill: ColorValue;
  totalColor: string;
}

const HIT_SLOP = {
  bottom: 8,
  left: 8,
  right: 8,
  top: 8,
};

export const ScanCartButton: React.FC<Props> = ({ fill, totalColor }) => {
  const { draftOrder } = useNewOrderContext();
  const { updateAndClearQuantity } = useNewOrderActionsContext();
  const { navigate } = useNavigation<Navigation>();
  const { showErrorToast } = useToasts();
  const { isSmallLayout } = useLayoutContext();

  const [loading, setLoading] = useState(false);
  const showCartButton = useShowCartButton();
  const cartSummaryText = useOrderButtonText();

  // Cart button is shown in drawer header on tablet
  return isSmallLayout && showCartButton ? (
    <TouchableOpacity
      accessibilityLabel={
        cartSummaryText
          ? `${i18n.t('view_cart')}, ${cartSummaryText}`
          : i18n.t('view_cart')
      }
      activeOpacity={0.8}
      disabled={loading}
      hitSlop={HIT_SLOP}
      onPress={async () => {
        Analytics.track('scan_cart_button.press', {
          orderUUID: draftOrder?.uuid,
        });
        setLoading(true);

        try {
          PerfTiming.start(PerfTimingEvent.ORDER_SUMMARY_VISIBLE, {
            entryPoint: 'scan_cart_button',
          });
          const result = await updateAndClearQuantity('scan_order_button');
          if (shouldShowOrder(result)) {
            navigate(APP_CONTAINER_ROUTES.NewOrder);
          }
        } catch (e) {
          showErrorToast();
        }

        setLoading(false);
      }}
      style={styles.container}
    >
      {loading ? (
        <ActivityIndicator color={fill} size="small" />
      ) : (
        <CartIcon fill={fill} stroke={fill} />
      )}
      {cartSummaryText ? (
        <View style={styles.summaryText}>
          <Typography
            color={totalColor}
            numberOfLines={1}
            variant="paragraph-xs"
          >
            {cartSummaryText}
          </Typography>
        </View>
      ) : null}
    </TouchableOpacity>
  ) : null;
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
  },
  // Caps the summary text to the width the header reserves per action
  // (see titleMargin in navigation-header-content.tsx) so a long value
  // can't push under the absolutely-centered title.
  summaryText: {
    maxWidth: ICON_MARGIN + ICON_WIDTH,
  },
});
